import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { CartItem, CartState, CartAction } from '../types/cart';

const CART_STORAGE_KEY = 'palacesamples_cart';

function loadCartFromStorage(): Pick<CartState, 'items' | 'total'> {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return { items: [], total: 0 };
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.items)) return { items: [], total: 0 };
    const rawItems = parsed.items as CartItem[];
    const dedupedMap = new Map<string, CartItem>();
    for (const item of rawItems) {
      if (!dedupedMap.has(item.id)) {
        dedupedMap.set(item.id, item);
      }
    }
    const items = Array.from(dedupedMap.values());
    const total = items.reduce((sum, item) => sum + item.price, 0);
    return { items, total };
  } catch {
    return { items: [], total: 0 };
  }
}

function saveCartToStorage(items: CartItem[], total: number) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ items, total }));
  } catch {
    // ignore
  }
}

interface CartContextType extends CartState {
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  toggleCart: () => void;
  isInCart: (itemId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const initialState: CartState = {
  items: [],
  total: 0,
  isOpen: false,
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const alreadyInCart = state.items.some((item) => item.id === action.payload.id);
      if (alreadyInCart) return state;
      const newItems = [...state.items, action.payload];
      return {
        ...state,
        items: newItems,
        total: newItems.reduce((sum, item) => sum + item.price, 0),
      };
    }
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      return {
        ...state,
        items: newItems,
        total: newItems.reduce((sum, item) => sum + item.price, 0),
      };
    }
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        total: 0,
      };
    case 'TOGGLE_CART':
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    default:
      return state;
  }
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState, (init) => {
    const { items, total } = loadCartFromStorage();
    return { ...init, items, total };
  });

  useEffect(() => {
    saveCartToStorage(state.items, state.total);
  }, [state.items, state.total]);

  const addItem = useCallback((item: CartItem) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  }, []);

  const isInCart = useCallback(
    (itemId: string) => state.items.some((item) => item.id === itemId),
    [state.items]
  );

  const removeItem = useCallback((itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const toggleCart = useCallback(() => {
    dispatch({ type: 'TOGGLE_CART' });
  }, []);

  return (
    <CartContext.Provider
      value={{
        ...state,
        addItem,
        removeItem,
        clearCart,
        toggleCart,
        isInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 